import { useCallback, useEffect, useRef, useState } from "react";
import { Sparkles } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { useApprovedTrends } from "@/hooks/useApprovedTrends";
import { useTrendNotifySettings } from "@/hooks/useTrendNotifySettings";
import {
  OPEN_TREND_MODAL_EVENT,
  TREND_DEEP_LINK_PARAM,
} from "@/lib/trendNotifications";

/**
 * Details modal opened by the "View trend" toast action, history entries, or a
 * shared `?trend=<id>` link. The open trend lives in the URL, so browser
 * back/forward opens and closes the modal, and Radix Dialog traps keyboard
 * focus inside it while open.
 */
export function TrendDetailsModal() {
  const { trends } = useApprovedTrends();
  const { markSeen } = useTrendNotifySettings();
  const [searchParams, setSearchParams] = useSearchParams();
  const trendId = searchParams.get(TREND_DEEP_LINK_PARAM);
  /** Element focused before the modal opened, so focus can be restored. */
  const restoreRef = useRef<HTMLElement | null>(null);
  const [pushedEntry, setPushedEntry] = useState(false);

  const openTrend = useCallback(
    (id: string) => {
      restoreRef.current = document.activeElement as HTMLElement | null;
      const next = new URLSearchParams(window.location.search);
      next.set(TREND_DEEP_LINK_PARAM, id);
      setPushedEntry(true);
      setSearchParams(next); // pushes a history entry so Back closes the modal
    },
    [setSearchParams],
  );

  const closeTrend = useCallback(() => {
    if (pushedEntry) {
      setPushedEntry(false);
      window.history.back(); // keeps Back/Forward symmetric
    } else {
      const next = new URLSearchParams(window.location.search);
      next.delete(TREND_DEEP_LINK_PARAM);
      setSearchParams(next, { replace: true });
    }
    const el = restoreRef.current;
    restoreRef.current = null;
    if (el && document.contains(el)) requestAnimationFrame(() => el.focus());
  }, [pushedEntry, setSearchParams]);

  useEffect(() => {
    const onOpen = (e: Event) => {
      const id = (e as CustomEvent<{ trendId: string }>).detail?.trendId;
      if (id) openTrend(id);
    };
    window.addEventListener(OPEN_TREND_MODAL_EVENT, onOpen as EventListener);
    return () => window.removeEventListener(OPEN_TREND_MODAL_EVENT, onOpen as EventListener);
  }, [openTrend]);

  // Opening the details counts as "viewed": no more toasts for this trend.
  useEffect(() => {
    if (trendId) markSeen(trendId);
  }, [trendId, markSeen]);

  const trend = trends?.find((t) => t.id === trendId) ?? null;
  const open = trendId !== null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && closeTrend()}>

      <DialogContent className="max-w-lg" data-testid="trend-details-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-left">
            <Sparkles className="size-4 text-primary" aria-hidden="true" />
            {trend?.title ?? "Trend details"}
          </DialogTitle>
          <DialogDescription className="text-left">
            {trend?.summary ?? "This trend is no longer in your curated feed."}
          </DialogDescription>
        </DialogHeader>

        {trend && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {trend.niche && <Badge variant="secondary">{trend.niche}</Badge>}
              {trend.tags.slice(0, 6).map((tag) => (
                <Badge key={tag} variant="outline">
                  {tag}
                </Badge>
              ))}
            </div>
            <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[
                ["Total", trend.total_score],
                ["Velocity", trend.velocity_score],
                ["Volume", trend.volume_score],
                ["Relevance", trend.relevance_score],
              ].map(([label, value]) => (
                <div key={String(label)} className="rounded-lg border border-border p-2">
                  <dt className="font-mono text-[10px] uppercase text-muted-foreground">{label}</dt>
                  <dd className="text-lg font-semibold">{value as number}</dd>
                </div>
              ))}
            </dl>
            {trend.source_slugs.length > 0 && (
              <p className="text-xs text-muted-foreground">
                Sources: {trend.source_slugs.join(", ")}
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
